import { Type } from 'class-transformer';
import {
	IsArray,
	IsEnum,
	IsInt,
	IsNumber,
	IsOptional,
	IsString,
	ValidateNested,
} from 'class-validator';
import { UnitOfMeasure } from '../../enums';
import type { LocationEntity, LocationItemEntity } from '../entity';
import { LocationStatus, LocationType } from '../entity';

export class CreateLocationDto {
	@IsOptional()
	@IsString()
	description?: string;

	@IsOptional()
	@IsString()
	code?: string;

	@IsOptional()
	@IsEnum(LocationType)
	type?: LocationType;

	@IsOptional()
	@IsInt()
	clientAddressId?: number;

	@IsOptional()
	@IsInt()
	warehouseId?: number;

	@IsOptional()
	@IsString()
	locationCode?: string;

	@IsOptional()
	@IsNumber()
	capacity?: number;

	@IsOptional()
	@IsEnum(UnitOfMeasure)
	units?: UnitOfMeasure;

	@IsOptional()
	@IsEnum(LocationStatus)
	status?: LocationStatus;
}

export class UpdateLocationDto extends CreateLocationDto {}

export class BulkCreateLocationDto {
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => CreateLocationDto)
	locations!: Array<CreateLocationDto>;
}

export class BulkUpdateLocationDto {
	@IsArray()
	@IsInt({ each: true })
	ids!: Array<number>;

	@ValidateNested()
	@Type(() => UpdateLocationDto)
	data!: UpdateLocationDto;
}

export class BulkDeleteLocationDto {
	@IsArray()
	@IsInt({ each: true })
	ids!: Array<number>;
}

export class CreateLocationItemDto {
	@IsInt()
	locationId!: number;

	@IsInt()
	itemId!: number;

	@IsOptional()
	@IsNumber()
	quantity?: number;

	// Count-based capacity (PD-13604): how many units of this item fit in the
	// bin. Sensor fill% × capacity → on-hand quantity.
	@IsOptional()
	@IsNumber()
	capacity?: number;
}

export class UpdateLocationItemDto {
	@IsOptional()
	@IsNumber()
	quantity?: number;

	// Count-based capacity (PD-13604) — see CreateLocationItemDto.capacity.
	@IsOptional()
	@IsNumber()
	capacity?: number;
}

export class BulkCreateLocationItemDto {
	@IsArray()
	@ValidateNested({ each: true })
	@Type(() => CreateLocationItemDto)
	items!: Array<CreateLocationItemDto>;
}

export type LocationDto = LocationEntity;
export type LocationItemDto = LocationItemEntity;

export interface LocationSearchResultDto {
	id: string;
	label: string;
	subtitle?: string;
	count?: number;
	type: 'city' | 'state' | 'zip' | 'address' | 'county';
	data?: unknown;
}

export interface LocationStateProvinceDto {
	id: string;
	label: string;
	type: string;
}

export interface LocationPaginationDto {
	page: number;
	limit: number;
	total: number;
	totalPages: number;
}

export type LocationSearchResponseDto =
	| {
			success: true;
			data: Array<LocationSearchResultDto>;
	  }
	| {
			success: false;
			error: string;
	  };

export type GetStatesResponseDto =
	| {
			success: true;
			data: Array<LocationStateProvinceDto>;
	  }
	| {
			success: false;
			error: string;
	  };

export type GetLocationsResponseDto =
	| {
			success: true;
			data: Array<LocationDto>;
			pagination: LocationPaginationDto;
	  }
	| {
			success: false;
			error: string;
	  };

export type GetLocationResponseDto =
	| {
			success: true;
			data: LocationDto;
	  }
	| {
			success: false;
			error: string;
	  };

export type CreateLocationResponseDto =
	| {
			success: true;
			data: LocationDto;
	  }
	| {
			success: false;
			error: string;
	  };

export type UpdateLocationResponseDto =
	| {
			success: true;
			data: LocationDto;
	  }
	| {
			success: false;
			error: string;
	  };

export type DeleteLocationResponseDto =
	| {
			success: true;
	  }
	| {
			success: false;
			error: string;
	  };

export type GetLocationItemsResponseDto =
	| {
			success: true;
			data: Array<LocationItemDto>;
			pagination: LocationPaginationDto;
	  }
	| {
			success: false;
			error: string;
	  };

export type CreateLocationItemResponseDto =
	| {
			success: true;
			data: LocationItemDto;
	  }
	| {
			success: false;
			error: string;
	  };

export type UpdateLocationItemResponseDto =
	| {
			success: true;
			data: LocationItemDto;
	  }
	| {
			success: false;
			error: string;
	  };

export type DeleteLocationItemResponseDto =
	| {
			success: true;
	  }
	| {
			success: false;
			error: string;
	  };

export type BulkCreateLocationItemsResponseDto =
	| {
			success: true;
			data: Array<LocationItemDto>;
	  }
	| {
			success: false;
			error: string;
	  };

export interface LocationLotDto {
	id: number;
	lotNumber?: string;
	[key: string]: unknown;
}

export type GetLocationLotsResponseDto =
	| {
			success: true;
			data: Array<LocationLotDto>;
			pagination: LocationPaginationDto;
	  }
	| {
			success: false;
			error: string;
	  };
